import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchAllAlarms } from "../../../api/AlarmApi";
import LatestAlarmMap from "../../../components/dashboard/LatestAlarmMap";
import AlarmDeliveryStats from "./AlarmDeliveryStats";

const AlarmDetailsView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const alarmId = Number(id);

  const { data: alarmsResponse, isLoading } = useQuery(
    ["allAlarms"],
    fetchAllAlarms,
    { staleTime: 60_000 },
  );

  const alarm = useMemo(() => {
    const list = alarmsResponse?.data;
    if (!Array.isArray(list)) return undefined;
    return list.find((a) => a.id === alarmId);
  }, [alarmsResponse, alarmId]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!alarm) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Larmet hittades inte.</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/admin/alarm")}>
          Tillbaka
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        width: "100%",
        maxWidth: 1600,
        mx: "auto",
        p: { xs: 2, md: 3, lg: 4 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, lg: 2.5 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
        }}
      >
        <Box>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin/alarm")}
            sx={{ mb: 0.5, ml: -1 }}
          >
            Tillbaka till larmhistorik
          </Button>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.2 }}
          >
            {alarm.alarm_type || "Larm"}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {new Date(alarm.started).toLocaleString("sv-SE", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: { xs: 2, lg: 2.5 },
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0, 1fr) minmax(340px, 420px)",
          },
          alignItems: "stretch",
          minHeight: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <LatestAlarmMap alarm={alarm} mapHeight={520} sideWidth={260} />
        </Box>
        <Box sx={{ minHeight: 0, minWidth: 0 }}>
          <AlarmDeliveryStats alarmId={alarmId} />
        </Box>
      </Box>
    </Box>
  );
};

export default AlarmDetailsView;
